import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";

/**
 * Shared hook for every /admin/settings/* sub-page. Loads the full site
 * settings document, exposes a partial setter and a save() that persists the
 * whole payload (the backend accepts the full object).
 */
export default function useSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  // Prevent re-fetching (and clobbering local edits) when React StrictMode
  // remounts the effect in development.
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    api.get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch((e) => setError(e.message));
  }, []);

  const set = useCallback((k, v) => {
    setSettings((prev) => ({ ...prev, [k]: v }));
    setSaved(false);
  }, []);

  const patch = useCallback((partial) => {
    setSettings((prev) => ({ ...prev, ...partial }));
    setSaved(false);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [settings]);

  return { settings, setSettings, set, patch, save, saving, saved, error };
}
